import { Backdrop } from '@mui/material';
import { revealKioskPinRequest } from 'api/requests/profile-requests';
import UPInput from 'components/input';
import {
  GeneralConfirmModalStyled,
  ModalActionButton,
  ModalActionContainer,
  ModalContainer,
  ModalContent,
} from 'components/general-confirm-modal/styles';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  GENERAL__CANCEL_BUTTON,
  GENERAL__CONFIRM,
  PROFILE__CURRENT_PASSWORD,
  PROFILE__PINCODE_REVEAL_TITLE,
  PROFILE__PINCODE_WRONG_PASSWORD,
} from 'translations/constants';

interface IRevealPincodeForm {
  password: string;
}

interface RevealPincodeModalProps {
  open: boolean;
  onClose: () => void;
  onRevealed: (pin: string) => void;
}

const RevealPincodeModal = ({ open, onClose, onRevealed }: RevealPincodeModalProps) => {
  const [t] = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [wrongPassword, setWrongPassword] = useState(false);

  const { control, handleSubmit, reset } = useForm<IRevealPincodeForm>({ defaultValues: { password: '' } });

  const handleClose = () => {
    reset({ password: '' });
    setWrongPassword(false);
    onClose();
  };

  const onSubmit = async ({ password }: IRevealPincodeForm) => {
    setSubmitting(true);
    setWrongPassword(false);
    try {
      const pin = await revealKioskPinRequest(password);
      reset({ password: '' });
      onRevealed(pin);
    } catch (e) {
      const httpStatus = (e as { response?: { status?: number } })?.response?.status;
      if (httpStatus === 401) {
        setWrongPassword(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Backdrop open={open} sx={{ zIndex: 2000 }}>
      <GeneralConfirmModalStyled open={open} onClose={handleClose}>
        <ModalContainer>
          <ModalContent>{t(PROFILE__PINCODE_REVEAL_TITLE)}</ModalContent>
          <Controller
            name="password"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <UPInput
                value={field.value}
                onChange={field.onChange}
                label={t(PROFILE__CURRENT_PASSWORD)}
                placeholder=""
                type="password"
                required
                error={wrongPassword}
                helperText={wrongPassword ? t(PROFILE__PINCODE_WRONG_PASSWORD) : ''}
              />
            )}
          />
          <ModalActionContainer>
            <ModalActionButton text={t(GENERAL__CONFIRM)} onClick={handleSubmit(onSubmit)} disabled={submitting} />
            <ModalActionButton text={t(GENERAL__CANCEL_BUTTON)} outlined onClick={handleClose} disabled={submitting} />
          </ModalActionContainer>
        </ModalContainer>
      </GeneralConfirmModalStyled>
    </Backdrop>
  );
};

export default RevealPincodeModal;
